import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { destinationForClaims, readClaims } from '../../hooks/useSession';
import { supabase } from '../../lib/supabase';

/**
 * Return handler for any Supabase email-link flow (magic link, password
 * recovery, email confirmation, invite). The client is configured with
 * `detectSessionInUrl: true`, so it auto-exchanges the URL hash for a
 * session on page load. We wait for that, then forward:
 *
 *   type=recovery  → /auth/reset-password      (force password update flow)
 *   otherwise      → ?next= query param, or /  (standard sign-in target)
 *
 * URL shape after a successful click:
 *   /auth/callback?next=/admin/dashboard#access_token=...&type=magiclink
 *   /auth/callback?next=/auth/reset-password#access_token=...&type=recovery
 *
 * The hash is stripped by the Supabase client once it's consumed.
 */
export function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  // On failure, send the user back to the RIGHT sign-in. AuthCallback is shared
  // by staff and bidder email links; the `next` target encodes the audience
  // (staff → /admin or /super paths; bidder → / or /auth/reset-password), so a
  // failed bidder link no longer dumps them on the tenant-staff sign-in.
  const nextParam = searchParams.get('next') ?? '';
  const backToSignIn =
    nextParam.startsWith('/admin') || nextParam.startsWith('/super') ? '/admin' : '/signin';

  useEffect(() => {
    // If Supabase already placed an error in the URL, surface it.
    const hash = window.location.hash.slice(1);
    const hashParams = new URLSearchParams(hash);
    const hashError = hashParams.get('error_description');
    if (hashError) {
      setError(hashError);
      return;
    }

    const linkType = hashParams.get('type');
    const queryNext = searchParams.get('next');

    // Poll briefly for the session (client consumes URL hash asynchronously
    // via onAuthStateChange on mount).
    let cancelled = false;
    const start = Date.now();
    const deadlineMs = 5000;

    async function waitForSession() {
      while (!cancelled && Date.now() - start < deadlineMs) {
        const { data } = await supabase.auth.getSession();
        if (data.session) {
          // Password recovery always wins — user hasn't updated credentials yet.
          // Otherwise honor explicit ?next=, falling back to role-based smart routing.
          const destination =
            linkType === 'recovery'
              ? '/auth/reset-password'
              : (queryNext ?? destinationForClaims(readClaims(data.session)));
          await navigate(destination, { replace: true });
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      if (!cancelled) {
        setError(
          'Sign-in link did not authenticate. The link may have expired or already been used.',
        );
      }
    }

    void waitForSession();
    return () => {
      cancelled = true;
    };
  }, [navigate, searchParams]);

  return (
    <main
      style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        textAlign: 'center',
      }}
    >
      {error ? (
        <div style={{ maxWidth: '32rem' }}>
          <h1
            style={{
              fontFamily: 'var(--font-heading-active)',
              fontSize: '1.5rem',
              fontWeight: 700,
              color: 'var(--text)',
              marginBottom: '0.5rem',
            }}
          >
            Sign-in failed
          </h1>
          <p style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>{error}</p>
          <a href={backToSignIn} style={{ color: 'var(--brand-primary)' }}>
            Back to sign-in
          </a>
        </div>
      ) : (
        <p style={{ color: 'var(--text-muted)' }}>Completing sign-in…</p>
      )}
    </main>
  );
}
